import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  InputBase,
  useTheme, 
  ClickAwayListener,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledLink,
} from "../../assets/css/styledComponent";
import { FaChevronDown } from "react-icons/fa";
import { BusinessCategory, BusinessData } from "../../types";
import {
  BusinessQueryNames,
  GET_ACTIVE_BUSINESS_CATEGORIES,
} from "../../graphql/queries";
import useLoadReduxStoreData from "../../redux/reducers/loadReduxStoreData";
import { useSelectBusinesses } from "../../redux/store";
import { setSelectedBusiness } from "../../redux/actions/businessActions";
import { searchCompanies } from "../../components/consumer/search/searchCompanies";
import CategoriesList from "../../components/common/categoriesList";
import BusinessList from "../../components/business/business/businessList";
import { useIsMobile } from '../../util/utils';
import { useNavigate } from "react-router-dom";

const CustomerHome = (props: any) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedBusinessCategory, setSelectedBusinessCategory] =
    useState<BusinessCategory | null>(null);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(
    false
  );

  const { data: activeBusinessCategories } = useQuery(
    GET_ACTIVE_BUSINESS_CATEGORIES,
    {
      variables: {
        countryId: Number(localStorage.getItem("currentCountryId")),
      },
    }
  );

  const currentCountryId = localStorage.getItem("currentCountryId") || "1";
  useLoadReduxStoreData(BusinessQueryNames.GET_BUSINESSES, {
    countryId: currentCountryId,
  });

  const businesses = useSelectBusinesses(); 

  const handleBusinessSelect = (business: BusinessData) => {
    dispatch(setSelectedBusiness(business.businessBasic.id));
  };

  const getCompaniesByBusinessCategory = () => {
    const filteredCompanies = selectedBusinessCategory
      ? businesses.filter((business: BusinessData) => {
          return (
            Number(business.businessBasic.businessCategoryId) ===
              Number(selectedBusinessCategory.id) &&
            (business.businessBasic.displayOnBookingPage === true ||
              business.businessBookingTypes?.some(
                (bookingType: { displayOnBookingPage: boolean }) =>
                  bookingType.displayOnBookingPage === true
              ))
          );
        })
      : businesses.filter((business: BusinessData) => {
          return (
            business.businessBasic.displayOnBookingPage === true ||
            business.businessBookingTypes?.some(
              (bookingType: { displayOnBookingPage: boolean }) =>
                bookingType.displayOnBookingPage === true
            )
          );
        });

    // Return the result after filtering with additional search query filtering, if any.
    return searchCompanies(filteredCompanies || [], searchQuery);
  };
  

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordion(isExpanded ? panel : false);
    };
  const handleClickAway = () => {
    setExpandedAccordion(false);
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Redirect to /search?q=mic (or whatever the search query is)
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <Box
      sx={{
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: "#22374a",
          color: theme.palette.primary.contrastText,
          textAlign: "center",
        }}
      >
        <Box>
          <Box>
            <Typography variant="h3" fontWeight="bold">
              Easy Booking
            </Typography>
            <Typography variant="subtitle1" sx={{ padding: "10px" }}>
              Discover the ideal events, services, classes, or businesses.
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxWidth: "600px",
              mx: "auto",
              mt: { xs: 0, sm: 3 },
              mb: { xs: 3, sm: 4 },
              backgroundColor: theme.palette.background.paper,
              borderRadius: "10px",
              padding: "0.5rem",
              width: "90%",
              boxShadow: theme.shadows[1],
            }}
          >
            <SearchIcon color="action" />
            <InputBase
              placeholder="Search for services, events, classes, or businesses"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ marginLeft: "1rem", color: theme.palette.text.primary }}
            />
          </Box>
          {/* Categories Section */}
          <Box
            sx={{
              width: "100%", // Full width of the parent container
              overflowX: "auto", // Allow horizontal scrolling if needed
              // Hide scrollbar for WebKit browsers (Chrome, Safari)
              "&::-webkit-scrollbar": {
                display: "none",
              },
              // Hide scrollbar for IE, Edge and Firefox
              msOverflowStyle: "none", // IE and Edge
              scrollbarWidth: "none", // Firefox
            }}
          >
            <CategoriesList />
          </Box>
        </Box>
      </Box>

      {/* Business List Section */}
      <Container sx={{ flexGrow: 1, paddingY: "2rem" }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Featured Businesses
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <BusinessList
              businesses={getCompaniesByBusinessCategory()}
              onSelect={handleBusinessSelect}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Accordion Section */}
      <Container maxWidth="lg">
        {/* Grid for accordions */}
        <Box style={{ margin: "0 60px 0 60px" }}>
          {activeBusinessCategories && (
            <ClickAwayListener onClickAway={handleClickAway}>
              <Grid container spacing={2} sx={{ padding: "2rem" }}>
                {activeBusinessCategories.getBookingTypesByActiveCities.map(
                  (cityData: any, index: number) => {
                    const panelId = `panel-${index}`;
                    if (cityData.categories.length > 0)
                      return (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                          <StyledAccordion
                            expanded={expandedAccordion === panelId}
                            onChange={handleAccordionChange(panelId)}
                            sx={{
                              boxShadow: "none",
                              backgroundColor: "transparent", // Removes the background color
                              "&:before": {
                                display: "none", // Removes the default Accordion divider line
                              },
                              "&.Mui-expanded": {
                                margin: 2,
                              },
                            }}
                          >
                            <StyledAccordionSummary
                              expandIcon={<FaChevronDown />}
                              aria-controls={`${panelId}-content`}
                              id={`${panelId}-header`}
                              sx={{
                                flexDirection: "row-reverse", // Moves the expandIcon to the left
                                padding: 0,
                                backgroundColor: "transparent", // Ensures the summary has no background
                                "& .MuiAccordionSummary-expandIconWrapper": {
                                  marginLeft: "1rem", // Space between the icon and text
                                  transform: "rotate(0deg)", // Initial state
                                  transition: "transform 0.3s ease", // Smooth rotation transition
                                },
                                "& .Mui-expanded .MuiAccordionSummary-expandIconWrapper":
                                  {
                                    transform: "rotate(180deg)", // Rotate icon when expanded
                                  },
                                "& .MuiAccordionSummary-content": {
                                  margin: 2,
                                },
                                "& .Mui-expanded .MuiAccordionSummary-content":
                                  {
                                    margin: 2, // Ensure margin stays consistent
                                  },
                                minHeight: "48px", // Set a consistent height for the summary
                                "&.Mui-expanded": {
                                  minHeight: "48px", // Same height when expanded
                                },
                              }}
                            >
                              <Typography>{cityData.city}</Typography>
                            </StyledAccordionSummary>

                            <StyledAccordionDetails
                              sx={{ padding: "0 0 0 1rem" }}
                            >
                              {cityData.categories.map(
                                (category: any, catIndex: number) => (
                                  <StyledLink
                                    href={`category/${
                                      category.id
                                    }_${category.name
                                      .replace(/\s+/g, "_")
                                      .toLowerCase()}?city=${cityData.cityId}_${
                                      cityData.city
                                    }`}
                                    key={catIndex}
                                    style={{
                                      textDecoration: "none",
                                      color: theme.palette.primary.light,
                                    }}
                                  >
                                    <Typography key={catIndex}>
                                      {category.name}
                                    </Typography>
                                  </StyledLink>
                                )
                              )}
                            </StyledAccordionDetails>
                          </StyledAccordion>
                        </Grid>
                      );
                  }
                )}
              </Grid>
            </ClickAwayListener>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default CustomerHome;
